import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { AlertCategoryCard } from './AlertCategoryCard';
import { AlertItemsTable, type AlertItemActions } from './AlertItemsTable';
import { DismissAlertModal } from './DismissAlertModal';
import { FixMissingDataModal } from './FixMissingDataModal';
import { FixLocationModal } from './FixLocationModal';
import { FixIntervalModal } from './FixIntervalModal';
import { DuplicateReviewModal } from './DuplicateReviewModal';
import { WorkOrderQuickDetail, ClientQuickDetail, ServiceLogQuickDetail } from './QuickDetailModal';
import {
  useDashboardSummary,
  useAlertItems,
  type AlertType,
} from '../../hooks/useDashboardAlerts';
import { api, type WorkOrder, type Client, type ServiceLog, type AgingClient, type MissingDataClient, type SuspiciousDataClient, type PendingInventoryReview, type AlertType as AlertDismissType } from '../../lib/api';
import { unwrapApi, failureMessage } from '../../lib/unwrapApi';
import { toast } from 'sonner';

export function OperationsDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Selected alert type for detail view
  const [selectedAlert, setSelectedAlert] = useState<AlertType | null>(null);

  // Collapsible left panel state
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);

  // Modal states
  const [dismissModal, setDismissModal] = useState<{
    isOpen: boolean;
    alertType: AlertDismissType;
    entityId: string;
    clientName: string;
    issueDescription: string;
  } | null>(null);

  const [fixMissingDataModal, setFixMissingDataModal] = useState<{
    isOpen: boolean;
    client: MissingDataClient;
  } | null>(null);

  const [fixLocationModal, setFixLocationModal] = useState<{
    isOpen: boolean;
    client: SuspiciousDataClient;
  } | null>(null);

  const [fixIntervalModal, setFixIntervalModal] = useState<{
    isOpen: boolean;
    client: SuspiciousDataClient;
  } | null>(null);

  const [duplicateReviewModal, setDuplicateReviewModal] = useState<{
    isOpen: boolean;
    client: SuspiciousDataClient;
  } | null>(null);

  // Quick detail modals (F3: Stay on dashboard, show detail in modal)
  const [workOrderDetailModal, setWorkOrderDetailModal] = useState<{
    isOpen: boolean;
    workOrder: WorkOrder;
  } | null>(null);

  const [clientDetailModal, setClientDetailModal] = useState<{
    isOpen: boolean;
    client: Client | AgingClient | MissingDataClient | SuspiciousDataClient;
  } | null>(null);

  const [serviceLogDetailModal, setServiceLogDetailModal] = useState<{
    isOpen: boolean;
    serviceLog: ServiceLog;
  } | null>(null);

  // Fetch client data for fix modals (location and interval)
  const clientIdForFix = fixLocationModal?.client.id || fixIntervalModal?.client.id;
  const { data: clientDataResponse } = useQuery({
    queryKey: ['client', clientIdForFix],
    queryFn: () => api.clients.getById(clientIdForFix!),
    enabled: !!clientIdForFix && (fixLocationModal?.isOpen || fixIntervalModal?.isOpen),
  });

  // Fetch dashboard summary
  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useDashboardSummary();

  // Fetch items for selected alert
  const { data: alertItems, isLoading: itemsLoading, refetch: refetchAlerts } = useAlertItems(selectedAlert);

  // Mutation for updating workorder status
  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      return api.workorders.updateStatus(id, status).then(unwrapApi);
    },
    onError: (error: unknown) => toast.error(failureMessage(error, 'Could not update the work order status')),
    onSuccess: () => {
      // Refetch alerts and summary
      refetchSummary();
      refetchAlerts();
      queryClient.invalidateQueries({ queryKey: ['workorders'] });
    },
  });

  // Mutation for undoing alert dismissal (F4 Phase 2)
  const undoDismissalMutation = useMutation({
    mutationFn: async (dismissalId: number) => {
      return api.alerts.undoDismissal(dismissalId).then(unwrapApi);
    },
    onError: (error: unknown) => toast.error(failureMessage(error, 'Could not undo the dismissal')),
    onSuccess: () => {
      // Refetch alerts and summary after undoing dismissal
      refetchSummary();
      refetchAlerts();
    },
  });

  // Format current date in Pacific timezone
  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Handle alert selection
  const handleAlertSelect = useCallback((alertType: AlertType) => {
    setSelectedAlert((prev) => (prev === alertType ? null : alertType));
  }, []);

  // Handle item click to open detail modal (F3: Stay on dashboard)
  const handleItemClick = useCallback((item: WorkOrder | Client | ServiceLog | AgingClient | MissingDataClient | SuspiciousDataClient | PendingInventoryReview) => {
    // Show quick detail modal instead of navigating away
    if ('status' in item && 'urgent' in item) {
      // It's a WorkOrder - show quick detail modal
      setWorkOrderDetailModal({
        isOpen: true,
        workOrder: item as WorkOrder,
      });
    } else if ('severity' in item && 'daysSinceService' in item) {
      // It's an AgingClient - show client quick detail
      setClientDetailModal({
        isOpen: true,
        client: item as AgingClient,
      });
    } else if ('missingFields' in item) {
      // It's a MissingDataClient - show client quick detail
      setClientDetailModal({
        isOpen: true,
        client: item as MissingDataClient,
      });
    } else if ('issueType' in item && 'issueDetail' in item) {
      // It's a SuspiciousDataClient
      if (item.issueType === 'duplicate_name') {
        // Open duplicate review modal
        setDuplicateReviewModal({
          isOpen: true,
          client: item as SuspiciousDataClient,
        });
      } else {
        // Show client quick detail
        setClientDetailModal({
          isOpen: true,
          client: item as SuspiciousDataClient,
        });
      }
    } else if ('clientName' in item && 'isInactive' in item) {
      // It's a Client - show quick detail
      setClientDetailModal({
        isOpen: true,
        client: item as Client,
      });
    } else if ('employeeId' in item && 'missedService' in item) {
      // It's a ServiceLog - show quick detail
      setServiceLogDetailModal({
        isOpen: true,
        serviceLog: item as ServiceLog,
      });
    } else if ('clientId' in item && 'changeCount' in item) {
      // It's a PendingInventoryReview - navigate to client inventory page
      navigate(`/inventory/client/${(item as PendingInventoryReview).clientId}`);
    }
  }, [navigate]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refetchSummary();
    refetchAlerts();
  }, [refetchSummary, refetchAlerts]);

  // Action handlers for inline actions
  const handleEdit = useCallback((item: WorkOrder | Client) => {
    if ('status' in item && 'urgent' in item) {
      // It's a WorkOrder - navigate to edit
      navigate(`/workorders?id=${item.id}&edit=true`);
    } else if ('clientName' in item && 'isInactive' in item) {
      // It's a Client - navigate to edit
      navigate(`/clients?id=${item.id}&edit=true`);
    }
  }, [navigate]);

  const handleStatusChange = useCallback((item: WorkOrder, newStatus: string) => {
    statusMutation.mutate({ id: item.id, status: newStatus });
  }, [statusMutation]);

  const handleAssign = useCallback((item: WorkOrder) => {
    // Navigate to workorder with assign mode
    navigate(`/workorders?id=${item.id}&assign=true`);
  }, [navigate]);

  const handleAddNote = useCallback((item: WorkOrder | Client | ServiceLog) => {
    // Navigate to the appropriate page with note mode
    if ('status' in item && 'urgent' in item) {
      navigate(`/workorders?id=${item.id}&note=true`);
    } else if ('clientName' in item && 'isInactive' in item) {
      navigate(`/clients?id=${item.id}&note=true`);
    } else if ('employeeId' in item && 'missedService' in item) {
      navigate(`/service-logs?id=${item.id}&note=true`);
    }
  }, [navigate]);

  // Alert dismiss/fix action handlers
  const handleDismissAlert = useCallback((item: MissingDataClient | SuspiciousDataClient | AgingClient, alertType: string) => {
    let issueDescription = '';
    if ('missingFields' in item) {
      issueDescription = `Missing fields: ${item.missingFields.join(', ')}`;
    } else if ('issueDetail' in item) {
      issueDescription = item.issueDetail || alertType;
    } else if ('daysSinceService' in item) {
      issueDescription = `${item.daysSinceService} days since last service (${item.intervalDays} day interval)`;
    }

    setDismissModal({
      isOpen: true,
      alertType: alertType as AlertDismissType,
      entityId: item.id,
      clientName: item.clientName,
      issueDescription,
    });
  }, []);

  const handleFixMissingData = useCallback((item: MissingDataClient) => {
    setFixMissingDataModal({
      isOpen: true,
      client: item,
    });
  }, []);

  const handleFixLocation = useCallback((item: SuspiciousDataClient) => {
    setFixLocationModal({
      isOpen: true,
      client: item,
    });
  }, []);

  const handleFixInterval = useCallback((item: SuspiciousDataClient) => {
    setFixIntervalModal({
      isOpen: true,
      client: item,
    });
  }, []);

  // Handler for undoing alert dismissal (F4 Phase 2)
  const handleUndoDismissal = useCallback((dismissalId: number) => {
    undoDismissalMutation.mutate(dismissalId);
  }, [undoDismissalMutation]);

  const handleModalClose = useCallback(() => {
    setDismissModal(null);
    setFixMissingDataModal(null);
    setFixLocationModal(null);
    setFixIntervalModal(null);
    setDuplicateReviewModal(null);
    setWorkOrderDetailModal(null);
    setClientDetailModal(null);
    setServiceLogDetailModal(null);
  }, []);

  const handleAlertFixed = useCallback(() => {
    // Refetch alerts and summary after fixing
    refetchSummary();
    refetchAlerts();
  }, [refetchSummary, refetchAlerts]);

  // Build actions object
  const alertActions: AlertItemActions = {
    onEdit: handleEdit,
    onStatusChange: handleStatusChange,
    onAssign: handleAssign,
    onAddNote: handleAddNote,
    onDismissAlert: handleDismissAlert,
    onFixMissingData: handleFixMissingData,
    onFixLocation: handleFixLocation,
    onFixInterval: handleFixInterval,
    onUndoDismissal: handleUndoDismissal,
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Page Header with date and actions */}
      <div className="bg-white border-b border-sage-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="font-display text-xl font-semibold text-forest-900">
              Operations Dashboard
            </h2>
            <span className="text-sage-500 text-sm hidden sm:inline">
              {formatDate()}
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Grand total badge */}
            {summary && (
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="text-sage-600 text-xs sm:text-sm hidden sm:inline">Total Alerts:</span>
                <Badge
                  variant={summary.grandTotal > 0 ? 'danger' : 'success'}
                  size="sm"
                >
                  {summary.grandTotal}
                </Badge>
              </div>
            )}

            <Button
              variant="secondary"
              size="sm"
              onClick={handleRefresh}
              title="Refresh"
            >
              <span className="sm:hidden">↻</span>
              <span className="hidden sm:inline">↻ Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6 h-full">
          {/* Alert Categories - Collapsible left column on desktop */}
          <div className={`flex-shrink-0 space-y-4 transition-all duration-300 ease-in-out ${
            isLeftPanelCollapsed ? 'lg:w-28' : 'lg:w-80'
          }`}>
            {/* Collapse toggle button - desktop only */}
            <button
              type="button"
              onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
              className="hidden lg:flex items-center justify-center w-full py-1 text-sage-500 hover:text-forest-700 hover:bg-sage-100 rounded transition-colors"
              title={isLeftPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
            >
              <svg
                className={`w-5 h-5 transition-transform ${isLeftPanelCollapsed ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              {!isLeftPanelCollapsed && <span className="text-xs ml-1">Collapse</span>}
            </button>

            {/* Alert cards - hide content when collapsed on desktop */}
            {/* Order: Clients, Workorders, Admin (per Item 5) */}
            <div className={`space-y-4 ${isLeftPanelCollapsed ? 'lg:hidden' : ''}`}>
              <AlertCategoryCard
                category="clients"
                summary={summary}
                selectedAlert={selectedAlert}
                onAlertSelect={handleAlertSelect}
                isLoading={summaryLoading}
              />
              <AlertCategoryCard
                category="workorders"
                summary={summary}
                selectedAlert={selectedAlert}
                onAlertSelect={handleAlertSelect}
                isLoading={summaryLoading}
              />
              <AlertCategoryCard
                category="admin"
                summary={summary}
                selectedAlert={selectedAlert}
                onAlertSelect={handleAlertSelect}
                isLoading={summaryLoading}
              />
            </div>

            {/* Collapsed view - show structured mini section cards */}
            {/* Order: Clients, Workorders, Admin (per Item 5) */}
            {isLeftPanelCollapsed && (
              <div className="hidden lg:flex flex-col gap-3 w-full">
                {summary && (
                  <>
                    {/* Clients section */}
                    <button
                      type="button"
                      onClick={() => { setIsLeftPanelCollapsed(false); }}
                      className="w-full p-2 rounded-lg hover:bg-sage-100 transition-colors text-left"
                      title={`Clients: ${summary.clients.total} alerts`}
                    >
                      <div className="text-[10px] font-medium text-sage-500 uppercase tracking-wide mb-1">
                        Clients
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">👥</span>
                        <Badge variant={summary.clients.total > 0 ? 'danger' : 'success'} size="sm">
                          {summary.clients.total}
                        </Badge>
                      </div>
                    </button>

                    {/* Work Orders section */}
                    <button
                      type="button"
                      onClick={() => { setIsLeftPanelCollapsed(false); }}
                      className="w-full p-2 rounded-lg hover:bg-sage-100 transition-colors text-left"
                      title={`Work Orders: ${summary.workorders.total} alerts`}
                    >
                      <div className="text-[10px] font-medium text-sage-500 uppercase tracking-wide mb-1">
                        Work Orders
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">📦</span>
                        <Badge variant={summary.workorders.total > 0 ? 'danger' : 'success'} size="sm">
                          {summary.workorders.total}
                        </Badge>
                      </div>
                    </button>

                    {/* Admin section */}
                    <button
                      type="button"
                      onClick={() => { setIsLeftPanelCollapsed(false); }}
                      className="w-full p-2 rounded-lg hover:bg-sage-100 transition-colors text-left"
                      title={`Admin: ${summary.admin.total} alerts`}
                    >
                      <div className="text-[10px] font-medium text-sage-500 uppercase tracking-wide mb-1">
                        Admin
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">⚙️</span>
                        <Badge variant={summary.admin.total > 0 ? 'warning' : 'success'} size="sm">
                          {summary.admin.total}
                        </Badge>
                      </div>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Alert Items Table - Right side, grows to fill */}
          <div className="flex-1 min-h-[400px] lg:min-h-0">
            <AlertItemsTable
              alertType={selectedAlert}
              data={alertItems ?? null}
              isLoading={itemsLoading}
              onItemClick={handleItemClick}
              actions={alertActions}
            />
          </div>
        </div>
      </main>

      {/* Modals */}
      {dismissModal && (
        <DismissAlertModal
          isOpen={dismissModal.isOpen}
          onClose={handleModalClose}
          alertType={dismissModal.alertType}
          entityId={dismissModal.entityId}
          clientName={dismissModal.clientName}
          issueDescription={dismissModal.issueDescription}
          onDismissed={handleAlertFixed}
        />
      )}

      {fixMissingDataModal && (
        <FixMissingDataModal
          isOpen={fixMissingDataModal.isOpen}
          onClose={handleModalClose}
          client={fixMissingDataModal.client}
          onFixed={handleAlertFixed}
        />
      )}

      {fixLocationModal && (
        <FixLocationModal
          isOpen={fixLocationModal.isOpen}
          onClose={handleModalClose}
          client={fixLocationModal.client}
          clientData={clientDataResponse?.data}
          onFixed={handleAlertFixed}
        />
      )}

      {fixIntervalModal && (
        <FixIntervalModal
          isOpen={fixIntervalModal.isOpen}
          onClose={handleModalClose}
          client={fixIntervalModal.client}
          currentInterval={clientDataResponse?.data?.intervalDays}
          onFixed={handleAlertFixed}
        />
      )}

      {duplicateReviewModal && (
        <DuplicateReviewModal
          isOpen={duplicateReviewModal.isOpen}
          onClose={handleModalClose}
          client={duplicateReviewModal.client}
          onClientClick={(clientId) => navigate(`/clients?id=${clientId}`)}
        />
      )}

      {/* Quick Detail Modals (F3: Stay on dashboard) */}
      {workOrderDetailModal && (
        <WorkOrderQuickDetail
          workOrder={workOrderDetailModal.workOrder}
          onClose={handleModalClose}
          onViewFull={() => {
            navigate(`/workorders?id=${workOrderDetailModal.workOrder.id}`);
            handleModalClose();
          }}
        />
      )}

      {clientDetailModal && (
        <ClientQuickDetail
          client={clientDetailModal.client as Client}
          onClose={handleModalClose}
          onViewFull={() => {
            navigate(`/clients?id=${clientDetailModal.client.id}`);
            handleModalClose();
          }}
        />
      )}

      {serviceLogDetailModal && (
        <ServiceLogQuickDetail
          serviceLog={serviceLogDetailModal.serviceLog}
          onClose={handleModalClose}
          onViewFull={() => {
            navigate(`/service-logs?id=${serviceLogDetailModal.serviceLog.id}`);
            handleModalClose();
          }}
        />
      )}
    </div>
  );
}
